import { AdCampaignPureCreateMutationInput, useMediaAdCampaignCreateMutation } from '@shared/api/graphql/generated';
import { BaseMutationResultType, FetchPolicyKeys } from '@shared/api/graphql/types';
import { AdCampaignTypeEnum, WeekDaysValues } from '@entities/media';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import TimezoneUtils from '@zvuk-b2b/react-uikit/toolkit/TimezoneUtils';

import { AdCampaignType } from './useAdCampaign';

export type UseAdCampaignCreateResultType = BaseMutationResultType & {
  data: {
    adCampaign?: AdCampaignType;
  };
};

export type UseAdCampaignCreateType = [
  request: (
    name: string,
    promoIds: string[],
    companyId: string,
    overrideValues?: Partial<AdCampaignPureCreateMutationInput>
  ) => Promise<AdCampaignType>,
  result: UseAdCampaignCreateResultType,
];

const days = [
  WeekDaysValues.Monday,
  WeekDaysValues.Tuesday,
  WeekDaysValues.Wednesday,
  WeekDaysValues.Thursday,
  WeekDaysValues.Friday,
  WeekDaysValues.Saturday,
  WeekDaysValues.Sunday,
];

const getInitialCampaignInput = (name: string, promoIds: string[], companyId: string) => ({
  companyId,
  title: name,
  type: AdCampaignTypeEnum.Interval,
  interval: 30,
  period: 30,
  startCompany: TimezoneUtils.getDateTimeWithoutTimezone(DateUtils.getStartOfDay(DateUtils.NOW)),
  endCompany: TimezoneUtils.getDateTimeWithoutTimezone(DateUtils.getEndOfDay(DateUtils.NOW)),
  startDayTime: '00:00',
  endDayTime: '23:59',
  days,
  pointIds: [],
  prevMediafileRequired: false,
  promos: promoIds.map((promoId) => ({
    promoId,
    promoAdId: '',
  })),
});

const useAdCampaignCreate = (): UseAdCampaignCreateType => {
  const [createMutation, createMutationResult] = useMediaAdCampaignCreateMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const request = async (
    name: string,
    promoIds: string[],
    companyId: string,
    overrideValues: Partial<AdCampaignPureCreateMutationInput> = {}
  ) => {
    const newAdCampaign = await createMutation({
      variables: {
        input: {
          ...getInitialCampaignInput(name, promoIds, companyId),
          ...overrideValues,
        },
      },
      refetchQueries: ['mediaAdCampaignsPaginationQuery'],
    });

    const result = newAdCampaign.data?.result;

    if (!result?.ok) {
      const backendMessage = result?.errors
        ?.flatMap((e) => e?.messages ?? [])
        .filter(Boolean)
        .join('. ');
      throw new Error(backendMessage || undefined);
    }

    return result.adCompany as AdCampaignType;
  };

  const result: UseAdCampaignCreateResultType = {
    error: createMutationResult.error,
    loading: createMutationResult.loading,
    called: createMutationResult.called,
    data: {
      adCampaign: createMutationResult.data?.result?.adCompany as AdCampaignType,
    },
  };

  return [request, result];
};

export default useAdCampaignCreate;
